import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfferSignPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/offer/${token}`)
      .then((r) => setOffer(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This offer link is invalid or has already been used."));
  }, [token]);

  const sign = async () => {
    if (!sig) { alert("Please draw your signature"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/offer/${token}/sign`, { signature_png: sig });
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Signing failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="max-w-md rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-600" data-testid="offer-error">{error}</p>
      </div>
    );
  }
  if (!offer) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="offer-sign-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-2xl items-center gap-2 px-6">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-9 w-auto" />
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">— Offer of Employment</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-6 py-8">
        {done ? (
          <div className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-8 text-center shadow-sm" data-testid="offer-signed-done">
            <CheckCircle size={40} weight="fill" className="mx-auto text-[#1e8449]" />
            <h1 className="mt-3 font-heading text-2xl font-semibold text-stone-900">Offer accepted — welcome aboard!</h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-stone-600">
              Your signed offer letter is on file, your references have been notified, and your employee account is ready. You're signed in — start orientation now:
            </p>
            <Button onClick={() => navigate("/orientation")}
              className="mt-5 rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="offer-start-orientation">
              Start orientation <ArrowRight size={15} className="ml-1.5" />
            </Button>
            <p className="mt-3 text-xs text-stone-400">Your login details are also in your email if you need them later.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="offer-welcome">
                Congratulations, {offer.applicant_name.split(" ")[0]}!
              </h1>
              <p className="mt-1 text-sm text-stone-500">Silver Home Care is pleased to offer you employment. Review the terms below and sign to accept.</p>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="offer-terms">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs uppercase tracking-wide text-stone-400">Position</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-position">{offer.position}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-stone-400">Pay rate</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-rate">${offer.pay_rate}/hour</p></div>
                <div><p className="text-xs uppercase tracking-wide text-stone-400">Anticipated start</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-start">{offer.start_date || "To be scheduled"}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-stone-400">Schedule</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-schedule">{offer.schedule_notes || "—"}</p></div>
                {offer.evv_bonus && <div><p className="text-xs uppercase tracking-wide text-stone-400">EVV bonus</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-evv-bonus">${offer.evv_bonus}/hour</p></div>}
                {offer.differential && <div><p className="text-xs uppercase tracking-wide text-stone-400">Shift differential</p><p className="mt-0.5 font-medium text-stone-900" data-testid="offer-differential">${offer.differential}/hour</p></div>}
              </div>
              {offer.notes && <p className="mt-4 border-t border-stone-100 pt-3 text-sm text-stone-600">{offer.notes}</p>}
              <div className="mt-4 space-y-2 border-t border-stone-100 pt-4 text-xs leading-relaxed text-stone-500">
                <p>This offer is contingent upon successful completion of all required pre-employment items, including background clearances, TB screening, I-9 verification, reference checks, and orientation training, as required by 28 Pa. Code Chapter 611 and applicable law.</p>
                <p className="rounded-lg border border-[#4A235A]/20 bg-[#4A235A]/[0.04] p-3 text-stone-600" data-testid="offer-references-note"><b>One quick favor that speeds everything up:</b> please let your references know we'll be reaching out — and ask them to give Kelly a call at <b>267-547-0310</b>. A quick heads-up from you makes those conversations go smoothly.</p>
                <p>Employment with Silver Home Care is "at-will" — either you or the Agency may end the employment relationship at any time, with or without cause or notice. Nothing in this letter creates a contract of employment. Overtime is paid at time-and-a-half for hours worked over 40 in a workweek, per the Fair Labor Standards Act and Pennsylvania law.</p>
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="offer-sign-block">
              <p className="font-heading text-sm font-semibold text-stone-900">Sign to accept</p>
              <p className="mt-1 text-xs text-stone-500">By signing you accept this offer of employment as described above.</p>
              <div className="mt-3">
                <SignaturePad onChange={setSig} />
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={sign} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="offer-sign-submit">
                  {busy ? "Signing…" : "Accept & sign offer"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
